import React, { useState, useEffect, useRef } from "react";
import { X, Building2, CheckCircle, Clock, AlertCircle, ArrowLeft, CreditCard, Banknote, Plus, Edit, Trash2, History, XCircle, ChevronDown, Crown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
// Select components removed - bank account linking now uses Coinflow iframe
import { useTheme } from "@/contexts/ThemeContext";
import { useLogoSrc } from '@/hooks/useLogoSrc';
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useGeoComplyTiming } from "@/hooks/useGeoComplyTiming";
import { apiClient } from "@/lib/api-client";
import { CoinflowCardNumberInput, CoinflowCvvInput } from '@coinflowlabs/react';
import { KYCSSNModal } from "./KYCSSNModal";
import { useIdComply } from "@/hooks/useIdComply";
import { useVerification } from "@/contexts/VerificationContext";

interface RedeemModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RedeemModal = ({ isOpen, onClose }: RedeemModalProps) => {
  const { getThemeColors } = useTheme();
  const logoSrc = useLogoSrc();
  const themeColors = getThemeColors();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { performCheck, isInitialized } = useGeoComplyTiming();
  const { getVerificationStatus } = useIdComply();
  const { checkVerificationAndPrompt } = useVerification();
  
  // Fetch user balance, limits, withdrawal methods, and redemption history
  useEffect(() => {
    // Wrapper to ensure no errors escape and crash the app
    const fetchData = async () => {
      setLoading(true);
      setWithdrawalOptions(null); // Reset so skeletons show while fetching
      try {
        // Fetch balance
        const balanceResponse = await apiClient.get('/redemption/balance');

        if (balanceResponse?.balance) {
          setBalanceData({
            sc_bonus_amount: balanceResponse.balance.sc_bonus_amount || 0,
            sc_promotional_amount: balanceResponse.balance.sc_promotional_amount || 0,
            sc_redeemable_amount: balanceResponse.balance.sc_redeemable_amount || 0,
          });
        }

        // Fetch withdrawal limits
        try {
          const limitsData = await apiClient.get('/redemption/limits');
          if (limitsData?.success && limitsData.limits) {
            setWithdrawalLimits(limitsData.limits);
          }
        } catch {
          // Error fetching limits
        }

        // Fetch enabled withdrawal options (methods + fees)
        try {
          const optionsData = await apiClient.get('/redemption/withdrawal-options');
          if (optionsData?.success && optionsData.options) {
            const optionsMap: any = {};
            optionsData.options.forEach((opt: any) => {
              optionsMap[opt.method] = { enabled: opt.enabled, feePercentage: opt.feePercentage };
            });
            const defaults = { enabled: true, feePercentage: 0 };
            setWithdrawalOptions({
              bank_account: optionsMap.bank_account || defaults,
              debit_card: optionsMap.debit_card || defaults,
            });
          }
        } catch {
          // Fallback — both enabled with default fees (0% bank, 5% debit card)
          setWithdrawalOptions({
            bank_account: { enabled: true, feePercentage: 0 },
            debit_card: { enabled: true, feePercentage: 0.05 },
          });
        }

        // Check if user is registered for withdrawals (lightweight check)
        try {
          const registrationStatus = await apiClient.get('/customer/withdrawal-methods/is-registered');
          const isRegistered = registrationStatus?.isRegistered === true;
          
          setIsUserRegistered(isRegistered);
          
          // Only fetch withdrawal methods if user is registered
          if (isRegistered) {
            try {
              const withdrawerData = await apiClient.get('/customer/withdrawal-methods');
              
              const accounts: any[] = [];
              
              // Extract bank accounts
              if (withdrawerData?.bankAccounts) {
                withdrawerData.bankAccounts.forEach((acc: any, index: number) => {
                  if (!acc.token) {
                    return; // Skip accounts without tokens
                  }
                  accounts.push({
                    id: acc.id || acc._id || `bank_${acc.token}`,
                    type: 'bank_account',
                    name: acc.bankName || 'Bank Account',
                    details: `****${acc.last4 || '****'}`,
                    accountId: acc.token, // Token is required for withdrawals
                    token: acc.token, // Token is required for deletion
                    isPrimary: accounts.length === 0,
                  });
                });
              }
              
              // Extract debit cards
              if (withdrawerData?.cards) {
                withdrawerData.cards.forEach((card: any, index: number) => {
                  if (!card.token) {
                    return; // Skip cards without tokens
                  }
                  accounts.push({
                    id: card.id || card._id || `card_${card.token}`,
                    type: 'debit_card',
                    name: `${card.brand || 'Card'} Debit`,
                    details: `****${card.last4 || '****'}`,
                    accountId: card.token, // Token is required for withdrawals
                    token: card.token, // Token is required for deletion
                    isPrimary: accounts.length === 0,
                  });
                });
              }
              
              setWithdrawalMethods(accounts);
              
              // Warn if some methods were skipped due to missing tokens
              const totalMethods = (withdrawerData?.bankAccounts?.length || 0) + (withdrawerData?.cards?.length || 0);
              // Some withdrawal methods may be skipped due to missing tokens
              
              // Ensure a payment method is selected if methods are available
              if (accounts.length > 0) {
                // If no method is selected, or the selected method doesn't exist in the new list, select the first one
                // Use string comparison to handle potential type mismatches
                const selectedExists = selectedPaymentMethod && 
                  accounts.some(acc => String(acc.id) === String(selectedPaymentMethod));
                if (!selectedPaymentMethod || !selectedExists) {
                  setSelectedPaymentMethod(String(accounts[0].id));
                }
              } else {
                // No methods available, clear selection
                setSelectedPaymentMethod('');
              }
            } catch (error: any) {
              // Error fetching withdrawal methods, but user is registered
              // User can still proceed even if fetching methods fails
              setWithdrawalMethods([]);
            }
          } else {
            // User is not registered - don't try to fetch withdrawal methods
            setWithdrawalMethods([]);
          }
        } catch (error: any) {
          // Error checking registration status - assume not registered to be safe
          setIsUserRegistered(false);
          setWithdrawalMethods([]);
        }

        // Fetch redemption history (initial load — pending tab, page 1)
        try {
          await fetchRequests('pending', 1);
        } catch (error) {
          // Silently fail for history - not critical for modal functionality
        }

      } catch (error) {
        // Catch any unexpected errors in the main try block
        // Only show toast for critical errors, not for individual endpoint failures
        // Individual endpoints handle their own errors above
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      // Explicitly handle promise rejection to prevent unhandled promise rejection errors
      // This is critical to prevent React error boundaries from catching unhandled rejections
      fetchData().catch((error) => {
        // This catch ensures no unhandled promise rejections
        // All errors should already be handled in fetchData, but this is a safety net
        try {
          setLoading(false);
          // Set safe defaults to ensure UI can still function
          if (isUserRegistered === null || isUserRegistered === undefined) {
            setIsUserRegistered(null);
          }
        } catch (setStateError) {
          // Even setState can fail in extreme cases
        }
        // Never throw - this would crash the app
      });
    }
  }, [isOpen, toast]);
  
  // State management
  const [currentView, setCurrentView] = useState<'main' | 'cashPrize' | 'addBank' | 'addCard' | 'requests'>('main');
  const [redemptionAmount, setRedemptionAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [requests, setRequests] = useState<any[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [requestsTab, setRequestsTab] = useState<'pending' | 'processing' | 'completed' | 'rejected'>('pending');
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsHasMore, setRequestsHasMore] = useState(false);
  const [loadingMoreRequests, setLoadingMoreRequests] = useState(false);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [balanceData, setBalanceData] = useState<{
    sc_bonus_amount: number;
    sc_promotional_amount: number;
    sc_redeemable_amount: number;
  }>({
    sc_bonus_amount: 0,
    sc_promotional_amount: 0,
    sc_redeemable_amount: 0,
  });
  
  // Verification status state
  const [verificationStatus, setVerificationStatus] = useState<{
    isEmailVerified: boolean;
    isDataVerified: boolean;
    isIdVerified: boolean;
  }>({
    isEmailVerified: false,
    isDataVerified: false,
    isIdVerified: false,
  });
  const [withdrawalLimits, setWithdrawalLimits] = useState<{ 
    minAmount: number; 
    maxAmount: number; 
    dailyCountLimit: number;
    tierName: string | null;
    tierColor: string | null;
    todayCount: number;
    todayAmount: number;
    pendingCount: number;
  } | null>(null);
  const [withdrawalMethods, setWithdrawalMethods] = useState<any[]>([]);
  const [feeInfo, setFeeInfo] = useState<{ fee: number; finalAmount: number; feePercentage: number } | null>(null);
  const [isKYCModalOpen, setIsKYCModalOpen] = useState(false);
  const [isUserRegistered, setIsUserRegistered] = useState<boolean | null>(null);
  
  // Selected withdrawal method type (bank or card) from the main view
  const [selectedMethodType, setSelectedMethodType] = useState<'bank_account' | 'debit_card' | null>(null);
  
  // Withdrawal options from backend (enabled/disabled + fee percentages)
  // null = not yet loaded (show skeleton), object = loaded
  const [withdrawalOptions, setWithdrawalOptions] = useState<{
    bank_account: { enabled: boolean; feePercentage: number };
    debit_card: { enabled: boolean; feePercentage: number };
  } | null>(null);
  
  // Collapsible request state
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);

  // Fetch requests for a specific tab/page
  const fetchRequests = async (tab: string, page: number, append: boolean = false) => {
    if (!append) setLoadingRequests(true);
    else setLoadingMoreRequests(true);
    try {
      const historyData = await apiClient.get(`/redemption/history?status=${tab}&page=${page}&limit=10`);
      if (historyData?.success) {
        if (append) {
          setRequests(prev => [...prev, ...(historyData.requestHistory || [])]);
        } else {
          setRequests(historyData.requestHistory || []);
        }
        setRequestsHasMore(historyData.hasMore || false);
        setRequestsPage(page);
        if (historyData.statusCounts) {
          setStatusCounts(historyData.statusCounts);
        }
      }
    } catch {
      // Error fetching requests
    } finally {
      setLoadingRequests(false);
      setLoadingMoreRequests(false);
    }
  };
  
  // Reset view state whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentView('main');
      setSelectedMethodType(null);
      setRedemptionAmount('');
      setExpandedRequestId(null);
      setRequestsTab('pending');
      setRequestsPage(1);
    }
  }, [isOpen]);

  // Swipe to close state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Swipe to close handlers - only from top area
  const handleDragHandleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    const touch = e.touches[0];
    dragStartY.current = touch.clientY;
    setIsDragging(true);
  };

  const handleDragHandleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragStartY.current;
    
    // Only allow downward dragging
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  };

  const handleDragHandleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.stopPropagation();
    
    const threshold = 100; // Close if dragged down more than 100px
    if (dragY > threshold) {
      onClose();
    } else {
      // Animate back to original position
      setDragY(0);
    }
    
    // Reset
    setIsDragging(false);
    dragStartY.current = 0;
  };

  // Reset drag state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  // Prevent pull-to-refresh when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store original values
      const originalOverscrollBehavior = document.body.style.overscrollBehaviorY;
      const originalOverscrollBehaviorHtml = document.documentElement.style.overscrollBehaviorY;
      
      // Prevent pull-to-refresh on body and html
      document.body.style.overscrollBehaviorY = 'none';
      document.documentElement.style.overscrollBehaviorY = 'none';
      
      return () => {
        // Restore original values
        document.body.style.overscrollBehaviorY = originalOverscrollBehavior;
        document.documentElement.style.overscrollBehaviorY = originalOverscrollBehaviorHtml;
      };
    }
  }, [isOpen]);

  // Helper function to safely format numbers with commas and 2 decimal places
  const formatNumber = (value: number | string | undefined | null): string => {
    const num = typeof value === 'number' ? value : parseFloat(String(value || 0)) || 0;
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Compact number format for mobile (e.g., 315K instead of 315,284.90)
  const formatCompactNumber = (value: number | string | undefined | null): string => {
    const num = typeof value === 'number' ? value : parseFloat(String(value || 0)) || 0;
    if (isNaN(num)) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toFixed(2);
  };

  // Calculate balance values - ensure all values are numbers
  const scBonus = parseFloat(String(balanceData.sc_bonus_amount || 0)) || 0;
  const scPromo = parseFloat(String(balanceData.sc_promotional_amount || 0)) || 0;
  const scRedeemable = parseFloat(String(balanceData.sc_redeemable_amount || 0)) || 0;
  
  const totalBalance = (isNaN(scBonus) ? 0 : scBonus) + (isNaN(scPromo) ? 0 : scPromo) + (isNaN(scRedeemable) ? 0 : scRedeemable);
  const unplayedBalance = (isNaN(scBonus) ? 0 : scBonus) + (isNaN(scPromo) ? 0 : scPromo);
  const redeemableBalance = isNaN(scRedeemable) ? 0 : scRedeemable;
  
  // Bank auth dialog states
  const [bankAuthIframeUrl, setBankAuthIframeUrl] = useState<string | null>(null);
  const [bankAuthLoading, setBankAuthLoading] = useState(false);
  const [bankAuthDialogOpen, setBankAuthDialogOpen] = useState(false);
  const [bankAuthIframeLoaded, setBankAuthIframeLoaded] = useState(false);
  
  const [cardForm, setCardForm] = useState({
    expMonth: '',
    expYear: '',
  });

  // Coinflow React SDK refs for debit card tokenization
  const coinflowCardNumberRef = useRef<{ getToken(): Promise<import('@coinflowlabs/react').CoinflowCardTokenResponse> } | null>(null);
  const cardWrapperRef = useRef<HTMLDivElement>(null);
  const cvvWrapperRef = useRef<HTMLDivElement>(null);

  const merchantId = import.meta.env.VITE_COINFLOW_MERCHANT_ID || '';
  const environment = (import.meta.env.VITE_COINFLOW_ENVIRONMENT || 'sandbox') as 'sandbox' | 'production';
  const isStaging = environment !== 'production';

  // Handle focus events from TokenEx iframe to show focus ring
  useEffect(() => {
    const handleFocus = (event: MessageEvent) => {
      if (event.data?.event === 'focus' || event.data?.event === 'cvvFocus') {
        const container = event.data?.container;
        if (container === 'tokenExCardNumber' && cardWrapperRef.current) {
          cardWrapperRef.current.classList.add('focused');
        } else if (container === 'tokenExCardCvv' && cvvWrapperRef.current) {
          cvvWrapperRef.current.classList.add('focused');
        }
      }
    };

    const handleBlur = (event: MessageEvent) => {
      if (event.data?.event === 'blur' || event.data?.event === 'cvvBlur') {
        const container = event.data?.container;
        if (container === 'tokenExCardNumber' && cardWrapperRef.current) {
          cardWrapperRef.current.classList.remove('focused');
        } else if (container === 'tokenExCardCvv' && cvvWrapperRef.current) {
          cvvWrapperRef.current.classList.remove('focused');
        }
      }
    };

    window.addEventListener('message', handleFocus);
    window.addEventListener('message', handleBlur);

    return () => {
      window.removeEventListener('message', handleFocus);
      window.removeEventListener('message', handleBlur);
    };
  }, []);
  
  // Ensure a payment method is selected when methods are available
  // Only depend on withdrawalMethods — use functional setState to read current selection
  // without adding selectedPaymentMethod to deps (which would re-fire on every selection change)
  useEffect(() => {
    if (withdrawalMethods.length > 0) {
      setSelectedPaymentMethod(prev => {
        const selectedExists = prev && 
          withdrawalMethods.some(m => String(m.id) === String(prev));
        return (!prev || !selectedExists) ? String(withdrawalMethods[0].id) : prev;
      });
    } else {
      setSelectedPaymentMethod('');
    }
  }, [withdrawalMethods]);

  // Load verification status
  useEffect(() => {
    if (isOpen) {
      const loadVerificationStatus = async () => {
        try {
          // Email is always verified for logged-in users (same as profile page)
          const isEmailVerified = true;
          
          // Fetch IDComply verification status
          const idComplyStatus = await getVerificationStatus();
          const isDataVerified = idComplyStatus?.dataVerificationStatus === 'completed' || 
                                idComplyStatus?.dataVerificationStatus === 'complete';
          const isIdVerified = idComplyStatus?.idVerificationStatus === 'completed';
          
          setVerificationStatus({
            isEmailVerified,
            isDataVerified,
            isIdVerified,
          });
        } catch {
          // Set defaults on error - email is always verified for logged-in users
          setVerificationStatus({
            isEmailVerified: true,
            isDataVerified: false,
            isIdVerified: false,
          });
        }
      };
      
      loadVerificationStatus();
    }
  }, [isOpen, getVerificationStatus]);
  
  const handleVerifyClick = () => {
    onClose();
    navigate('/profile');
  };

  const handleCashPrizeRedeem = (methodType?: 'bank_account' | 'debit_card') => {
    // Check ID verification before allowing redemption flow
    if (!checkVerificationAndPrompt('id')) {
      onClose(); // Close the modal so verification can show
      return;
    }
    
    // Store the selected method type early so it persists through KYC flow
    if (methodType) {
      setSelectedMethodType(methodType);
    }
    
    // Check if user is registered before allowing cash redemption
    if (isUserRegistered === false) {
      setIsKYCModalOpen(true);
      return;
    }
    if (redeemableBalance <= 0) {
      toast({
        title: "Insufficient Balance",
        description: "You don't have any redeemable balance.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    if (methodType) {
      // Auto-select the first method of this type
      const firstOfType = withdrawalMethods.find(m => m.type === methodType);
      if (firstOfType) {
        setSelectedPaymentMethod(String(firstOfType.id));
      }
    }
    setCurrentView('cashPrize');
  };

  const handleBackToMain = () => {
    setCurrentView('main');
    setRedemptionAmount('');
    setSelectedMethodType(null);
  };

  const handleBackToCashPrize = () => {
    setCurrentView('cashPrize');
  };

  const handleAmountChange = (value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      setRedemptionAmount('');
    } else if (numValue > redeemableBalance) {
      setRedemptionAmount(redeemableBalance.toString());
    } else {
      setRedemptionAmount(value);
    }
  };

  // Load Coinflow Bank Auth iframe URL when entering the addBank view
  const loadBankAuthUrl = async () => {
    try {
      setBankAuthLoading(true);
      // allowedSpeeds=standard limits to bank accounts only (no card)
      const result = await apiClient.get('/customer/withdrawal-methods/bank-auth-url?allowedSpeeds=standard');
      if (result?.iframeUrl) {
        setBankAuthIframeUrl(result.iframeUrl);
      } else {
        throw new Error('Failed to get bank auth URL');
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to load bank linking. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      setCurrentView('cashPrize');
    } finally {
      setBankAuthLoading(false);
    }
  };

  // Listen for accountLinked postMessage from Coinflow Bank Auth iframe
  useEffect(() => {
    const handleBankAuthMessage = async (event: MessageEvent) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (data?.data === 'accountLinked') {
          
          toast({
            title: "Success",
            description: "Bank account linked successfully",
            variant: "success",
            duration: 3000,
          });

          // Refresh withdrawal methods from Coinflow
          try {
            const withdrawerData = await apiClient.get('/customer/withdrawal-methods');
            const accounts: any[] = [];
            
            if (withdrawerData?.bankAccounts) {
              withdrawerData.bankAccounts.forEach((acc: any) => {
                if (!acc.token) return;
                accounts.push({
                  id: acc.id || acc._id || `bank_${acc.token}`,
                  type: 'bank_account',
                  name: acc.bankName || 'Bank Account',
                  details: `****${acc.last4 || '****'}`,
                  accountId: acc.token,
                  token: acc.token,
                  isPrimary: accounts.length === 0,
                });
              });
            }
            
            if (withdrawerData?.cards) {
              withdrawerData.cards.forEach((card: any) => {
                if (!card.token) return;
                accounts.push({
                  id: card.id || card._id || `card_${card.token}`,
                  type: 'debit_card',
                  name: `${card.brand || 'Card'} Debit`,
                  details: `****${card.last4 || '****'}`,
                  accountId: card.token,
                  token: card.token,
                  isPrimary: accounts.length === 0,
                });
              });
            }
            
            setWithdrawalMethods(accounts);
            if (accounts.length > 0) {
              const newAccount = accounts.find(a => a.type === 'bank_account');
              if (newAccount) {
                setSelectedPaymentMethod(String(newAccount.id));
              }
            }
          } catch {
            // Error refreshing withdrawal methods
          }

          // Close the bank auth dialog
          closeBankAuthDialog();
        }
      } catch {
        // Not a JSON message or not relevant - ignore
      }
    };

    if (currentView === 'addBank' || bankAuthDialogOpen) {
      window.addEventListener('message', handleBankAuthMessage);
    }
    return () => {
      window.removeEventListener('message', handleBankAuthMessage);
    };
  }, [currentView, bankAuthDialogOpen, toast]);

  const handleAddBankAccount = async () => {
    // Check ID verification before allowing payment method addition
    if (!checkVerificationAndPrompt('id')) {
      onClose(); // Close the modal so verification can show
      return;
    }

    // Open the bank auth dialog natively (outside the redeem modal)
    setBankAuthDialogOpen(true);
    setBankAuthIframeLoaded(false);
    loadBankAuthUrl();
  };

  const closeBankAuthDialog = () => {
    setBankAuthDialogOpen(false);
    setBankAuthIframeUrl(null);
    setBankAuthIframeLoaded(false);
  };

  const handleAddDebitCard = async () => {
    // Check ID verification before allowing payment method addition
    if (!checkVerificationAndPrompt('id')) {
      onClose(); // Close the modal so verification can show
      return;
    }
    
    // Validate form
    if (!cardForm.expMonth || !cardForm.expYear) {
      toast({
        title: "Validation Error",
        description: "Please fill in expiration month and year",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      
      // Tokenize card using Coinflow React SDK components (client-side - PCI compliant)
      if (!coinflowCardNumberRef.current) {
        throw new Error('Card input component not initialized');
      }

      // Get token from card number input (CVV is included via CoinflowCvvInput)
      const cardTokenResponse = await coinflowCardNumberRef.current.getToken();
      if (!cardTokenResponse.token) {
        throw new Error('Failed to tokenize card number');
      }
      
      // Use the card number token (CVV is included when CoinflowCvvInput is used)
      const token = cardTokenResponse.token;

      // Add debit card via withdrawal methods endpoint
      try {
        await apiClient.post('/customer/withdrawal-methods/debit-card', {
          cardToken: token,
          expYear: cardForm.expYear,
          expMonth: cardForm.expMonth,
        });
      } catch (error: any) {
        // Check if error is due to missing KYC registration
        const errorMessage = error?.message || 
                            error?.response?.data?.message || 
                            error?.response?.data?.error || 
                            '';
        const isKYCRequired = errorMessage.includes('KYC_REQUIRED');
        
        if (isKYCRequired) {
          setIsKYCModalOpen(true);
          setLoading(false);
          return;
        }
        throw error;
      }

      toast({
        title: "Success",
        description: "Debit card added successfully",
        variant: "success",
        duration: 3000,
      });

      // Refresh withdrawal methods
      const withdrawerData = await apiClient.get('/customer/withdrawal-methods');
      const accounts: any[] = [];
      
      if (withdrawerData?.bankAccounts) {
        withdrawerData.bankAccounts.forEach((acc: any) => {
          if (!acc.token) {
            return; // Skip accounts without tokens
          }
          accounts.push({
            id: acc.id || acc._id || `bank_${acc.token}`,
            type: 'bank_account',
            name: acc.bankName || 'Bank Account',
            details: `****${acc.last4 || '****'}`,
            accountId: acc.token, // Token is required for withdrawals
            token: acc.token, // Token is required for deletion
            isPrimary: accounts.length === 0,
          });
        });
      }
      
      if (withdrawerData?.cards) {
        withdrawerData.cards.forEach((card: any) => {
          if (!card.token) {
            return; // Skip cards without tokens
          }
          accounts.push({
            id: card.id || card._id || `card_${card.token}`,
            type: 'debit_card',
            name: `${card.brand || 'Card'} Debit`,
            details: `****${card.last4 || '****'}`,
            accountId: card.token, // Token is required for withdrawals
            token: card.token, // Token is required for deletion
            isPrimary: accounts.length === 0,
          });
        });
      }
      
      setWithdrawalMethods(accounts);
      if (accounts.length > 0) {
        const newCard = accounts.find(a => a.type === 'debit_card');
        if (newCard) {
          setSelectedPaymentMethod(String(newCard.id));
        }
      }

      setCardForm({ expMonth: '', expYear: '' });
      setCurrentView('cashPrize');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add debit card. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePaymentMethod = async (methodId: string) => {
    try {
      const method = withdrawalMethods.find(m => m.id === methodId);
      if (!method) return;

      if (!method.token) {
        toast({
          title: "Error",
          description: "Cannot delete: token not found",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }

      // Delete from Coinflow using token
      if (method.type === 'bank_account') {
        await apiClient.delete(`/customer/withdrawal-methods/bank-account/${method.token}`);
      } else if (method.type === 'debit_card') {
        await apiClient.delete(`/customer/withdrawal-methods/card/${method.token}`);
      }

      // Update local state
      const updatedMethods = withdrawalMethods.filter(m => String(m.id) !== String(methodId));
      setWithdrawalMethods(updatedMethods);
      
      // If the removed method was selected, select the first available method or clear selection
      if (String(selectedPaymentMethod) === String(methodId)) {
        setSelectedPaymentMethod(updatedMethods.length > 0 ? String(updatedMethods[0].id) : '');
      }

      toast({
        title: "Success",
        description: "Withdrawal method removed",
        variant: "success",
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to remove withdrawal method",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleCreateRequest = async (type: 'cash' | 'gift', amount: number, method?: string) => {
    // Check ID verification before allowing redemption
    if (!checkVerificationAndPrompt('id')) {
      onClose(); // Close the modal so verification can show
      return;
    }
    
    /**
     * GEOCOMPLY INTEGRATION: Check user location before redemption
     * 
     * REQUIREMENT: Verify user location before allowing redemption requests
     * 
     * This ensures users cannot create redemption requests from non-compliant jurisdictions.
     * The check happens before creating the request to prevent:
     * - Users redeeming from restricted locations
     * - Compliance violations during redemption processing
     * 
     * FLOW:
     * 1. Check if GeoComply SDK is initialized
     * 2. Perform geolocation check
     * 3. If non-compliant, block redemption request and show error
     * 4. If compliant, proceed with redemption request creation
     */
    if (isInitialized) {
      const isCompliant = await performCheck('create a redemption request');
      if (!isCompliant) {
        return; // Block redemption if location check fails
      }
    }

    try {
      // For cash redemptions, we need withdrawal method and account ID
      if (type === 'cash') {
        const selectedMethod = withdrawalMethods.find(m => String(m.id) === String(selectedPaymentMethod));
        if (!selectedMethod) {
          throw new Error('Please select a withdrawal method');
        }

        // Validate that the selected method has a token
        if (!selectedMethod.accountId || !selectedMethod.token) {
          throw new Error('Selected payment method is missing required token. Please remove and re-add this payment method.');
        }

        const withdrawalMethod = selectedMethod.type === 'debit_card' ? 'debit_card' : 'bank_account';
        // Extract last 4 digits from the method details (format: "****1234")
        const last4Match = selectedMethod.details?.match(/(\d{4})$/);
        const withdrawalMethodLast4 = last4Match ? last4Match[1] : undefined;
        
        const data = await apiClient.post('/redemption/request', {
          amount: amount,
          withdrawalMethod: withdrawalMethod,
          accountId: selectedMethod.accountId, // This should be the token
          description: `Cash redemption via ${withdrawalMethod === 'debit_card' ? 'debit card' : 'bank account'}`,
          withdrawalMethodLast4,
        });

        if (!data?.success) {
          throw new Error(data?.error || 'Failed to create redemption request');
        }

        // Show success toast notification
        toast({
          title: "✅ Request Created Successfully!",
          description: `Your cash redemption request has been submitted. An admin will review it within 24 hours.`,
          variant: "success",
          duration: 5000,
        });
      } else {
        // Gift card redemption (existing flow)
        const data = await apiClient.post('/redemption/request', {
          amount: amount,
          description: `Gift Card redemption - ${method || 'Email'}`
        });

        if (!data?.success) {
          throw new Error(data?.error || 'Failed to create redemption request');
        }

        toast({
          title: "✅ Request Created Successfully!",
          description: `Your gift card redemption request has been submitted. An admin will review it within 24 hours.`,
          variant: "success",
          duration: 5000,
        });
      }
      
      setCurrentView('requests');
      setRequestsTab('pending');
      setRedemptionAmount('');
      
      // Refresh requests data for the pending tab
      await fetchRequests('pending', 1);

      // Refetch balance and limits so counts/amounts are accurate if user creates another request
      try {
        const [balanceResponse, limitsData] = await Promise.all([
          apiClient.get('/redemption/balance'),
          apiClient.get('/redemption/limits'),
        ]);
        if (balanceResponse?.balance) {
          setBalanceData({
            sc_bonus_amount: balanceResponse.balance.sc_bonus_amount || 0,
            sc_promotional_amount: balanceResponse.balance.sc_promotional_amount || 0,
            sc_redeemable_amount: balanceResponse.balance.sc_redeemable_amount || 0,
          });
        }
        if (limitsData?.success && limitsData.limits) {
          setWithdrawalLimits(limitsData.limits);
        }
      } catch (e) {
        // Non-critical — limits will refresh next time modal opens
      }
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create redemption request. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const redemptionAmountNumber = parseFloat(redemptionAmount) || 0;
  // Ensure minAmount and maxAmount are always numbers
  const minAmount = parseFloat(String(withdrawalLimits?.minAmount || 10)) || 10;
  const maxAmountValue = parseFloat(String(withdrawalLimits?.maxAmount || 0)) || 0;

  // Daily limit checks
  const dailyCountLimit = withdrawalLimits?.dailyCountLimit || 0;
  const dailyAmountLimit = withdrawalLimits?.maxAmount || 0;
  const todayCount = withdrawalLimits?.todayCount || 0;
  const todayAmount = withdrawalLimits?.todayAmount || 0;
  const pendingCount = withdrawalLimits?.pendingCount || 0;
  // Count limit is based on ALL pending/processing requests (not just today's)
  const isCountLimitReached = dailyCountLimit > 0 && pendingCount >= dailyCountLimit;
  const isAmountLimitReached = dailyAmountLimit > 0 && todayAmount >= dailyAmountLimit;
  // Effective amount the user can still redeem right now (tier remaining vs wallet balance)
  const remainingDailyAmount = dailyAmountLimit > 0 ? Math.max(0, dailyAmountLimit - todayAmount) : Infinity;
  const effectiveRedeemable = Math.min(redeemableBalance, remainingDailyAmount);
  const isDailyLimitReached = isCountLimitReached || isAmountLimitReached;

  // maxAmount accounts for VIP tier limit, wallet balance, AND daily remaining amount
  const maxAmount = maxAmountValue > 0
    ? Math.min(maxAmountValue, redeemableBalance, remainingDailyAmount)
    : Math.min(redeemableBalance, remainingDailyAmount);

  // Improved validation: properly handle string to number conversion and edge cases
  const parsedAmount = redemptionAmount ? parseFloat(redemptionAmount) : NaN;
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount >= minAmount && parsedAmount <= maxAmount && parsedAmount > 0;
  const filteredWithdrawalMethods = selectedMethodType
    ? withdrawalMethods.filter(m => m.type === selectedMethodType)
    : withdrawalMethods;
  const hasPaymentMethods = filteredWithdrawalMethods.length > 0;
  
  // Verify that the selected payment method actually exists in the filtered methods array
  const selectedMethodExists = hasPaymentMethods && selectedPaymentMethod && 
    filteredWithdrawalMethods.some(m => String(m.id) === String(selectedPaymentMethod));

  // Calculate fees from withdrawal options (no API call needed)
  useEffect(() => {
    const amount = parseFloat(redemptionAmount);
    if (!amount || amount <= 0 || !withdrawalOptions || !selectedMethodType) {
      setFeeInfo(null);
      return;
    }

    // Use fee percentage from withdrawal options based on selected method type
    const feePercentage = selectedMethodType === 'debit_card'
      ? withdrawalOptions.debit_card.feePercentage
      : withdrawalOptions.bank_account.feePercentage;

    setFeeInfo({
      fee: amount * feePercentage,
      finalAmount: amount * (1 - feePercentage),
      feePercentage: feePercentage * 100,
    });
  }, [redemptionAmount, selectedMethodType, withdrawalOptions]);

  const tierColor = withdrawalLimits?.tierColor || '#a855f7';

  // Short screen = viewport height ≤ 700px (iPhone SE, small Androids)
  // We use a CSS variable approach: short screens get compact sizes, tall phones get roomy sizes
  // Desktop (lg:) is never affected
  // Shorthand: "sh" = short height class via max-h arbitrary variant

  const renderMainView = () => (
    <div className="space-y-4 short:space-y-2.5 md:space-y-5 lg:space-y-4 max-w-full mx-auto">
      {/* Verification Status */}
      <div className="bg-card/60 backdrop-blur-md p-3 short:p-2 md:p-5 lg:p-4 rounded-xl md:rounded-2xl border border-border/50">
        <div className="grid grid-cols-3 gap-2 short:gap-1.5 md:gap-4 lg:gap-3 text-center">
          {[
            { label: 'Email', verified: verificationStatus.isEmailVerified },
            { label: 'Data', verified: verificationStatus.isDataVerified },
            { label: 'ID', verified: verificationStatus.isIdVerified },
          ].map((item) => (
            <div key={item.label} className="py-3 px-2 short:py-2 md:py-5 md:px-3 lg:p-3 rounded-lg md:rounded-xl bg-muted/30 border border-border/40">
              {item.verified ? (
                <>
                  <CheckCircle className="h-5 w-5 short:h-4 short:w-4 md:h-7 md:w-7 lg:h-5 lg:w-5 text-casino-green mx-auto mb-1.5 short:mb-1 md:mb-2" />
                  <div className="text-xs short:text-[10px] md:text-base lg:text-xs font-medium text-foreground">{item.label}</div>
                  <div className="text-[10px] short:text-[9px] md:text-sm lg:text-[11px] text-green-400 font-medium">Verified</div>
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 short:h-4 short:w-4 md:h-7 md:w-7 lg:h-5 lg:w-5 text-casino-gold mx-auto mb-1.5 short:mb-1 md:mb-2" />
                  <div className="text-xs short:text-[10px] md:text-base lg:text-xs font-medium text-foreground">{item.label}</div>
                  <button onClick={handleVerifyClick} className="text-[10px] short:text-[9px] md:text-sm lg:text-[11px] text-primary hover:underline font-medium">
                    Verify
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Balance Overview */}
      <div className="bg-card/60 backdrop-blur-md p-3 short:p-2 md:p-5 lg:p-4 rounded-xl md:rounded-2xl border border-border/50">
        <div className="grid grid-cols-3 gap-2 short:gap-1.5 md:gap-4 lg:gap-3 text-center">
          <div className="py-3 px-2 short:py-2 md:py-5 md:px-3 lg:p-3 rounded-lg md:rounded-xl bg-muted/30 border border-border/40">
            <div className="text-[10px] md:text-xs lg:text-[11px] text-muted-foreground mb-1 short:mb-0.5 md:mb-1.5 lg:mb-1 font-medium uppercase tracking-wide">Balance</div>
            <div className="font-bold text-sm short:text-xs md:text-xl lg:text-base text-foreground leading-tight">
              <span className="md:hidden">{formatCompactNumber(totalBalance)}</span>
              <span className="hidden md:inline">SC {formatNumber(totalBalance)}</span>
            </div>
          </div>
          <div className="py-3 px-2 short:py-2 md:py-5 md:px-3 lg:p-3 rounded-lg md:rounded-xl bg-muted/30 border border-border/40">
            <div className="text-[10px] md:text-xs lg:text-[11px] text-muted-foreground mb-1 short:mb-0.5 md:mb-1.5 lg:mb-1 font-medium uppercase tracking-wide">Unplayed</div>
            <div className="font-bold text-sm short:text-xs md:text-xl lg:text-base text-foreground leading-tight">
              <span className="md:hidden">{formatCompactNumber(unplayedBalance)}</span>
              <span className="hidden md:inline">SC {formatNumber(unplayedBalance)}</span>
            </div>
          </div>
          <div className="py-3 px-2 short:py-2 md:py-5 md:px-3 lg:p-3 rounded-lg md:rounded-xl bg-green-500/15 border border-green-400/40">
            <div className="text-[10px] md:text-xs lg:text-[11px] text-casino-green mb-1 short:mb-0.5 md:mb-1.5 lg:mb-1 font-bold uppercase tracking-wide">Redeemable</div>
            <div className="font-bold text-sm short:text-xs md:text-xl lg:text-base text-casino-green leading-tight">
              <span className="md:hidden">{formatCompactNumber(redeemableBalance)}</span>
              <span className="hidden md:inline">SC {formatNumber(redeemableBalance)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* VIP Tier Withdrawal Limits */}
      {withdrawalLimits && (
        <div
          className="relative overflow-hidden rounded-xl md:rounded-2xl border px-4 py-3 short:px-3 short:py-2 md:px-5 md:py-4 lg:px-4 lg:py-3"
          style={{
            background: `linear-gradient(135deg, ${tierColor}15 0%, rgba(15,23,42,0.6) 60%, ${tierColor}08 100%)`,
            borderColor: `${tierColor}30`,
            boxShadow: `0 0 20px ${tierColor}10`,
          }}
        >
          {/* Shimmer accent */}
          <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
            <div
              className="absolute inset-0 animate-shimmer opacity-30"
              style={{
                background: `linear-gradient(110deg, transparent 30%, ${tierColor}20 50%, transparent 70%)`,
              }}
            />
          </div>
          <div className="relative flex items-center gap-3 short:gap-2 md:gap-4 lg:gap-3">
            <div
              className="h-9 w-9 short:h-7 short:w-7 md:h-12 md:w-12 lg:h-9 lg:w-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${tierColor}, ${tierColor}80)`,
                boxShadow: `0 2px 10px ${tierColor}50`,
              }}
            >
              <Crown className="h-4 w-4 short:h-3.5 short:w-3.5 md:h-5 md:w-5 lg:h-4 lg:w-4 text-white drop-shadow-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-black text-xs short:text-[11px] md:text-base lg:text-xs uppercase tracking-wide" style={{ color: tierColor }}>
                {withdrawalLimits.tierName || 'VIP'} Tier
              </span>
              <div className="text-[11px] short:text-[10px] md:text-sm lg:text-[11px] text-muted-foreground mt-0.5 md:mt-1 leading-snug">
                Up to {dailyCountLimit > 0 ? `${dailyCountLimit} withdrawal${dailyCountLimit !== 1 ? 's' : ''}` : 'unlimited withdrawals'} &middot; {dailyAmountLimit > 0 ? `SC ${formatNumber(dailyAmountLimit)}` : 'Unlimited'} per day
              </div>
            </div>
          </div>
          {/* Daily usage progress — stacked on small mobile, side by side on tablet+ */}
          <div className="relative grid grid-cols-1 gap-2 short:gap-1.5 mt-3 short:mt-2 md:grid-cols-2 md:gap-4 md:mt-4 lg:grid-cols-2 lg:gap-3 lg:mt-2.5">
            <div className="rounded-lg md:rounded-xl bg-foreground/10 px-3 py-2 short:px-2.5 short:py-1.5 md:px-4 md:py-3 lg:px-3 lg:py-2">
              <div className="flex items-center justify-between mb-1.5 short:mb-1 md:mb-2">
                <span className="text-[10px] short:text-[9px] md:text-xs lg:text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Pending Requests</span>
                <span className="text-xs short:text-[10px] md:text-base lg:text-[11px] font-bold text-foreground">{pendingCount}<span className="text-muted-foreground font-normal">/{dailyCountLimit > 0 ? dailyCountLimit : '∞'}</span></span>
              </div>
              {dailyCountLimit > 0 && (
                <div className="h-1.5 short:h-1 md:h-2 lg:h-1.5 rounded-full bg-muted/50 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min((pendingCount / dailyCountLimit) * 100, 100)}%`,
                      background: pendingCount >= dailyCountLimit
                        ? 'linear-gradient(90deg, hsl(var(--casino-gold)), hsl(var(--casino-red)))'
                        : `linear-gradient(90deg, ${tierColor}90, ${tierColor})`,
                      boxShadow: pendingCount >= dailyCountLimit ? '0 0 6px #ef444460' : `0 0 6px ${tierColor}40`,
                    }}
                  />
                </div>
              )}
            </div>
            <div className="rounded-lg md:rounded-xl bg-foreground/10 px-3 py-2 short:px-2.5 short:py-1.5 md:px-4 md:py-3 lg:px-3 lg:py-2">
              <div className="flex items-center justify-between mb-1.5 short:mb-1 md:mb-2">
                <span className="text-[10px] short:text-[9px] md:text-xs lg:text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Amount Today</span>
                <span className="text-xs short:text-[10px] md:text-base lg:text-[11px] font-bold text-foreground">
                  SC {formatNumber(todayAmount)}{dailyAmountLimit > 0 && <span className="text-muted-foreground font-normal">/{formatNumber(dailyAmountLimit)}</span>}
                </span>
              </div>
              {dailyAmountLimit > 0 && (
                <div className="h-1.5 short:h-1 md:h-2 lg:h-1.5 rounded-full bg-muted/50 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min((todayAmount / dailyAmountLimit) * 100, 100)}%`,
                      background: todayAmount >= dailyAmountLimit
                        ? 'linear-gradient(90deg, hsl(var(--casino-gold)), hsl(var(--casino-red)))'
                        : `linear-gradient(90deg, ${tierColor}90, ${tierColor})`,
                      boxShadow: todayAmount >= dailyAmountLimit ? '0 0 6px #ef444460' : `0 0 6px ${tierColor}40`,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          {isDailyLimitReached && (
            <div className="relative flex items-center gap-1.5 mt-2.5 short:mt-1.5 md:mt-3 pt-2.5 short:pt-1.5 md:pt-3 border-t text-[11px] short:text-[10px] md:text-sm lg:text-[11px] text-amber-400" style={{ borderColor: `${tierColor}20` }}>
              <AlertCircle className="h-3.5 w-3.5 short:h-3 short:w-3 md:h-4 md:w-4 lg:h-3.5 lg:w-3.5 flex-shrink-0" />
              <span>
                {isCountLimitReached
                  ? `You have ${pendingCount} pending request${pendingCount !== 1 ? 's' : ''}. Please wait for ${pendingCount === 1 ? 'it' : 'them'} to be processed before creating new ones.`
                  : `You've redeemed SC ${formatNumber(todayAmount)} of your SC ${formatNumber(dailyAmountLimit)} daily limit. Try again tomorrow.`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Redemption Options — Bank or Debit Card (only show enabled methods) */}
      <div>
        <h3 className="font-bold text-foreground text-xs md:text-base lg:text-sm uppercase tracking-wide mb-3 short:mb-2 md:mb-4 lg:mb-3">Redeem via</h3>
        
        {!withdrawalOptions ? (
          /* Skeleton loading state — show until withdrawal options are fetched */
          <div className="grid grid-cols-2 gap-3 short:gap-2 md:gap-4 lg:gap-3">
            {[0, 1].map((i) => (
              <div key={i} className="bg-card/60 backdrop-blur-md p-5 short:p-3 md:p-8 lg:p-6 rounded-xl md:rounded-2xl border border-border/50 text-center animate-pulse">
                <div className="h-11 w-11 short:h-9 short:w-9 md:h-16 md:w-16 lg:h-12 lg:w-12 mx-auto mb-2 short:mb-1.5 md:mb-3 lg:mb-2.5 rounded-full bg-muted/40" />
                <div className="h-4 short:h-3.5 md:h-5 lg:h-4 w-24 short:w-20 md:w-32 lg:w-24 mx-auto bg-muted/40 rounded mb-1.5 lg:mb-1.5" />
                <div className="h-3 short:h-2.5 md:h-4 lg:h-3 w-28 short:w-24 md:w-36 lg:w-28 mx-auto bg-muted/30 rounded" />
              </div>
            ))}
          </div>
        ) : (!withdrawalOptions.bank_account.enabled && !withdrawalOptions.debit_card.enabled) ? (
          <div className="bg-card/60 backdrop-blur-md p-5 short:p-4 md:p-8 lg:p-6 rounded-xl md:rounded-2xl border border-border/50 text-center">
            <AlertCircle className="h-6 w-6 short:h-5 short:w-5 md:h-8 md:w-8 lg:h-6 lg:w-6 text-muted-foreground mx-auto mb-2 md:mb-3" />
            <p className="text-xs md:text-base lg:text-sm text-muted-foreground">Withdrawals are currently unavailable. Please check back later.</p>
          </div>
        ) : (
          <div className={`grid gap-3 short:gap-2 md:gap-4 lg:gap-3 ${withdrawalOptions.bank_account.enabled && withdrawalOptions.debit_card.enabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {withdrawalOptions.bank_account.enabled && (
              <button
                type="button"
                disabled={isDailyLimitReached}
                className={`bg-card/60 backdrop-blur-md p-5 short:p-3 md:p-8 lg:p-6 rounded-xl md:rounded-2xl border border-border/50 transition-all duration-200 text-center group ${
                  isDailyLimitReached
                    ? 'opacity-40 cursor-not-allowed'
                    : 'hover:border-blue-400/40 hover:bg-blue-500/5 active:scale-[0.97]'
                }`}
                onClick={() => !isDailyLimitReached && handleCashPrizeRedeem('bank_account')}
              >
                <div className="h-11 w-11 short:h-9 short:w-9 md:h-16 md:w-16 lg:h-12 lg:w-12 mx-auto mb-2 short:mb-1.5 md:mb-3 lg:mb-2.5 rounded-full bg-blue-500/15 border border-blue-400/30 flex items-center justify-center">
                  <Building2 className="h-5 w-5 short:h-4 short:w-4 md:h-8 md:w-8 lg:h-6 lg:w-6 text-blue-400" />
                </div>
                <div className="font-semibold text-sm short:text-xs md:text-xl lg:text-base text-foreground">Bank Account</div>
                <div className="text-[11px] short:text-[10px] md:text-base lg:text-xs text-muted-foreground mt-1 short:mt-0.5 md:mt-2 lg:mt-1">
                  {withdrawalOptions.bank_account.feePercentage === 0 ? 'FREE' : 'Withdraw Fees Apply'} &middot; 3-10 business days after approval
                </div>
              </button>
            )}
            
            {withdrawalOptions.debit_card.enabled && (
              <button
                type="button"
                disabled={isDailyLimitReached}
                className={`bg-card/60 backdrop-blur-md p-5 short:p-3 md:p-8 lg:p-6 rounded-xl md:rounded-2xl border border-border/50 transition-all duration-200 text-center group ${
                  isDailyLimitReached
                    ? 'opacity-40 cursor-not-allowed'
                    : 'hover:border-purple-400/40 hover:bg-purple-500/5 active:scale-[0.97]'
                }`}
                onClick={() => !isDailyLimitReached && handleCashPrizeRedeem('debit_card')}
              >
                <div className="h-11 w-11 short:h-9 short:w-9 md:h-16 md:w-16 lg:h-12 lg:w-12 mx-auto mb-2 short:mb-1.5 md:mb-3 lg:mb-2.5 rounded-full bg-purple-500/15 border border-purple-400/30 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 short:h-4 short:w-4 md:h-8 md:w-8 lg:h-6 lg:w-6 text-purple-400" />
                </div>
                <div className="font-semibold text-sm short:text-xs md:text-xl lg:text-base text-foreground">Debit Card</div>
                <div className="text-[11px] short:text-[10px] md:text-base lg:text-xs text-muted-foreground mt-1 short:mt-0.5 md:mt-2 lg:mt-1">
                  {withdrawalOptions.debit_card.feePercentage === 0 ? 'FREE' : 'Withdraw Fees Apply'} &middot; ~30 min after approval
                </div>
              </button>
            )}
          </div>
        )}
        <div className="text-[11px] short:text-[10px] md:text-sm lg:text-xs text-muted-foreground text-center mt-2 short:mt-1.5 md:mt-3 lg:mt-2">Min. SC {formatNumber(minAmount)}</div>
      </div>
      
      {/* View Requests Link */}
      <div className="text-center pt-3 short:pt-2 md:pt-4 lg:pt-3 border-t border-border/50">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setCurrentView('requests')}
          className="text-muted-foreground hover:text-foreground text-sm short:text-xs md:text-base lg:text-sm h-10 short:h-8 md:h-12 lg:h-9 rounded-full transition-colors font-medium"
        >
          <History className="h-4 w-4 short:h-3.5 short:w-3.5 md:h-5 md:w-5 lg:h-3.5 lg:w-3.5 mr-1.5" />
          {requests.length > 0 ? `Requests (${requests.length})` : 'View Requests'}
        </Button>
      </div>
    </div>
  );

  const renderCashPrizeView = () => (
    <div className="space-y-3 md:space-y-5 lg:space-y-4 max-w-full mx-auto">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={handleBackToMain}
        className="h-8 md:h-10 lg:h-9 px-2 md:px-3 lg:px-3 -ml-1 lg:ml-0 text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 md:h-5 md:w-5 lg:h-5 lg:w-5 mr-1.5" />
        <span className="text-sm md:text-base lg:text-sm font-medium">Back</span>
      </Button>

      {/* Available Balance - Compact */}
      <div className="bg-gradient-to-br from-green-500/20 via-emerald-500/15 to-green-600/20 backdrop-blur-md px-4 py-3 md:px-6 md:py-5 lg:px-5 lg:py-3.5 rounded-xl md:rounded-2xl border border-green-400/40">
        <div className="text-center">
          <div className="text-lg md:text-3xl lg:text-2xl font-black text-casino-green drop-shadow-lg">SC {formatNumber(effectiveRedeemable)}</div>
          <div className="text-xs md:text-base lg:text-sm text-casino-green/70 font-medium mt-0.5 md:mt-1">Available to redeem today</div>
        </div>
      </div>

      <div className="space-y-3 md:space-y-5 lg:space-y-4">
        {/* Amount Input */}
        <div>
          <Label htmlFor="redemption-amount" className="text-xs md:text-sm lg:text-sm font-bold text-foreground mb-1.5 md:mb-2 lg:mb-2 block uppercase tracking-wide">
            Enter SC Amount
          </Label>
          <div className="flex gap-2 md:gap-3">
            <div className="flex-1">
              <Input
                id="redemption-amount"
                type="number"
                placeholder={minAmount.toString()}
                value={redemptionAmount}
                onChange={(e) => handleAmountChange(e.target.value)}
                className="text-sm md:text-base lg:text-base h-10 md:h-12 lg:h-10"
                min={minAmount}
                max={maxAmount}
                step="0.01"
              />
            </div>
            <div className="flex gap-1.5 md:gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs md:text-sm lg:text-sm h-10 md:h-12 lg:h-10 px-3 md:px-5 lg:px-4 rounded-lg"
                onClick={() => setRedemptionAmount(minAmount.toString())}
              >
                Min
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs md:text-sm lg:text-sm h-10 md:h-12 lg:h-10 px-3 md:px-5 lg:px-4 rounded-lg"
                onClick={() => setRedemptionAmount(effectiveRedeemable.toString())}
              >
                Max
              </Button>
            </div>
          </div>
          <div className="text-xs md:text-sm lg:text-xs text-muted-foreground mt-1.5 md:mt-2 font-medium">
            Min: SC {formatNumber(minAmount)} | Max: SC {formatNumber(maxAmount)}
            {maxAmount < redeemableBalance && (
              <span className="text-yellow-600"> ({remainingDailyAmount < (maxAmountValue > 0 ? maxAmountValue : Infinity) ? 'Daily Limit' : 'VIP Limit'})</span>
            )}
          </div>
        </div>

        {/* Payment Method Section — filtered by selectedMethodType */}
        {(() => {
          const filteredMethods = selectedMethodType
            ? withdrawalMethods.filter(m => m.type === selectedMethodType)
            : withdrawalMethods;
          const hasFiltered = filteredMethods.length > 0;
          const isBankType = selectedMethodType === 'bank_account';
          const typeLabel = isBankType ? 'Bank Account' : 'Debit Card';
          const addHandler = isBankType ? handleAddBankAccount : () => setCurrentView('addCard');

          return (
            <div className="bg-card/60 backdrop-blur-md p-3 md:p-5 lg:p-4 rounded-xl md:rounded-2xl border border-border/50">
              <div className="flex items-center justify-between mb-2 md:mb-3 lg:mb-2.5">
                <div className="flex items-center gap-2">
                  <h4 className="text-xs md:text-base lg:text-sm font-bold text-foreground uppercase tracking-wide">
                    {selectedMethodType ? `Select ${typeLabel}` : 'Payment Method'}
                  </h4>
                  {filteredMethods.length > 3 && (
                    <span className="text-[10px] md:text-xs text-muted-foreground font-medium">{filteredMethods.length} saved</span>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 md:h-9 px-2 md:px-3 text-xs md:text-sm rounded-full"
                  onClick={addHandler}
                >
                  <Plus className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1" />
                  Add
                </Button>
              </div>
              
              {hasFiltered ? (
                <div className="space-y-1.5 md:space-y-2 max-h-[140px] md:max-h-[180px] lg:max-h-[160px] overflow-y-auto scrollbar-thin">
                  {filteredMethods.map((method) => {
                    const isSelected = String(selectedPaymentMethod) === String(method.id);
                    return (
                    <div 
                      key={method.id}
                      className={`flex items-center gap-2 md:gap-2.5 p-2 md:p-3 lg:p-2.5 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-emerald-500/10 border-emerald-500/40' 
                          : 'bg-muted/20 border-transparent hover:bg-muted/40'
                      }`}
                      onClick={() => {
                        setSelectedPaymentMethod(String(method.id));
                      }}
                    >
                      <div className="flex items-center gap-2 md:gap-2.5 flex-1 min-w-0">
                         {method.type === 'bank_account' ? (
                           <div className="h-7 w-7 md:h-9 md:w-9 lg:h-8 lg:w-8 rounded-full bg-blue-500/15 border border-blue-400/30 flex items-center justify-center flex-shrink-0">
                             <Building2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-blue-400" />
                           </div>
                         ) : (
                           <div className="h-7 w-7 md:h-9 md:w-9 lg:h-8 lg:w-8 rounded-full bg-purple-500/15 border border-purple-400/30 flex items-center justify-center flex-shrink-0">
                             <CreditCard className="h-3.5 w-3.5 md:h-4 md:w-4 text-purple-400" />
                           </div>
                         )}
                         <div className="flex-1 min-w-0">
                           <div className="text-xs md:text-sm lg:text-xs font-semibold text-foreground truncate">{method.name}</div>
                           <div className="text-[11px] md:text-xs text-muted-foreground truncate">{method.details}</div>
                         </div>
                         {method.isPrimary && (
                           <Badge className="bg-green-500/20 text-green-400 text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 flex-shrink-0 rounded-full border border-green-500/30">Primary</Badge>
                         )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 md:h-8 md:w-8 p-0 text-red-400/60 hover:text-red-300 hover:bg-red-500/15 rounded-full flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePaymentMethod(method.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3 md:h-3.5 md:w-3.5" />
                      </Button>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-3 md:py-5">
                  <p className="text-xs md:text-sm text-muted-foreground">No {typeLabel.toLowerCase()}s linked yet. Use the + Add button above to get started.</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Fee Breakdown */}
        {redemptionAmount && isValidAmount && feeInfo && (
          <div className="bg-card/60 backdrop-blur-md p-3 md:p-5 lg:p-4 rounded-xl md:rounded-2xl border border-border/50">
            <h4 className="text-xs md:text-base lg:text-sm font-bold text-foreground mb-1.5 md:mb-2.5 lg:mb-2 uppercase tracking-wide">Transaction Summary</h4>
            <div className="space-y-1 md:space-y-2 text-xs md:text-base lg:text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Redemption Amount:</span>
                  <span className="font-semibold text-foreground">SC {formatNumber(redemptionAmount)}</span>
                </div>
                {feeInfo.fee === 0 ? (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Fee:</span>
                    <span className="font-semibold text-green-400">FREE</span>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Fee ({feeInfo.feePercentage.toFixed(2)}%):
                    </span>
                    <span className="font-semibold text-red-400">-SC {formatNumber(feeInfo.fee)}</span>
                  </div>
                )}
                <div className="border-t border-border/50 pt-1.5 md:pt-2.5 mt-1.5 md:mt-2.5">
                  <div className="flex justify-between font-bold">
                    <span className="text-foreground">You will receive:</span>
                    <span className="text-green-400 text-sm md:text-lg lg:text-base">${formatNumber(feeInfo.finalAmount)} USD</span>
                  </div>
                </div>
              </div>
          </div>
        )}

        {/* Submit Button */}
        <Button 
          className="w-full h-10 md:h-14 lg:h-11 bg-gradient-to-r from-green-600 via-emerald-500 to-green-600 hover:from-green-500 hover:via-green-600 hover:to-green-700 text-white font-bold rounded-lg md:rounded-xl shadow-lg border border-green-400/30 hover:border-green-300/50 transition-all duration-200 text-sm md:text-lg lg:text-base uppercase tracking-wide relative overflow-hidden group/btn" 
          disabled={!isValidAmount || !hasPaymentMethods || !selectedMethodExists}
          onClick={() => {
            handleCreateRequest('cash', redemptionAmountNumber);
          }}
        >
          <span className="relative z-10">Create Redemption Request</span>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000"></div>
        </Button>
        
        {/* Error Messages */}
        {!isValidAmount && redemptionAmount && (
          <div className="text-xs md:text-sm text-red-400 text-center bg-red-950/20 px-3 md:px-4 py-2 md:py-3 rounded-lg border border-red-900/50">
            {(() => {
              const parsed = parseFloat(redemptionAmount);
              if (isNaN(parsed)) {
                return 'Please enter a valid number';
              }
              if (parsed < minAmount) {
                return `Minimum: SC ${formatNumber(minAmount)}`;
              }
              if (parsed > maxAmount) {
                return `Maximum: SC ${formatNumber(maxAmount)}`;
              }
              return `Amount must be between SC ${formatNumber(minAmount)} and SC ${formatNumber(maxAmount)}`;
            })()}
          </div>
        )}
        {!selectedMethodExists && hasPaymentMethods && (
          <div className="text-xs md:text-sm text-yellow-500 text-center bg-yellow-950/20 px-3 md:px-4 py-2 md:py-3 rounded-lg border border-yellow-900/50">
            Please select a payment method
          </div>
        )}
        {!hasPaymentMethods && (
          <div className="text-xs md:text-sm text-muted-foreground text-center bg-muted/40 px-3 md:px-4 py-2 md:py-3 rounded-lg border border-border">
            {selectedMethodType === 'bank_account' ? 'Link a bank account to continue' :
             selectedMethodType === 'debit_card' ? 'Add a debit card to continue' :
             'Add a withdrawal method to continue'}
          </div>
        )}
      </div>
    </div>
  );

  const renderAddBankView = () => (
    <div className="space-y-4 md:space-y-5 lg:space-y-5 max-w-full mx-auto h-full flex flex-col">
      <div className="flex items-center gap-3 md:gap-4 lg:gap-4 mb-1">
        <Button variant="ghost" size="sm" onClick={() => { setBankAuthIframeUrl(null); handleBackToCashPrize(); }} className="h-10 md:h-12 lg:h-10 text-muted-foreground hover:text-foreground hover:bg-muted/50">
          <ArrowLeft className="h-5 w-5 md:h-6 md:w-6 lg:h-5 lg:w-5" />
        </Button>
        <div className="flex items-center gap-2 md:gap-3 lg:gap-3">
          <Building2 className="h-6 w-6 md:h-8 md:w-8 lg:h-6 lg:w-6 text-blue-400" />
          <h3 className="font-black text-base md:text-xl lg:text-lg text-foreground">Link Bank Account</h3>
        </div>
      </div>

      {bankAuthLoading && (
        <div className="flex-1 flex items-center justify-center min-h-[300px] md:min-h-[400px]">
          <div className="flex flex-col items-center gap-3 md:gap-4">
            <div className="w-8 h-8 md:w-10 md:h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm md:text-base text-muted-foreground">Loading secure bank linking...</p>
          </div>
        </div>
      )}

      {bankAuthIframeUrl && !bankAuthLoading && (
        <div className="flex-1 min-h-[450px] md:min-h-[550px] rounded-lg md:rounded-xl overflow-hidden border border-border/50">
          <iframe
            src={bankAuthIframeUrl}
            title="Link Bank Account"
            className="w-full h-full min-h-[450px] md:min-h-[550px]"
            style={{ border: 'none', minHeight: '450px' }}
            allow="payment;camera;clipboard-write"
          />
        </div>
      )}

      {!bankAuthIframeUrl && !bankAuthLoading && (
        <div className="flex-1 flex items-center justify-center min-h-[300px] md:min-h-[400px]">
          <div className="flex flex-col items-center gap-3 md:gap-4">
            <p className="text-sm md:text-base text-muted-foreground">Failed to load bank linking. Please go back and try again.</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderAddCardView = () => (
    <div className="space-y-4 md:space-y-5 lg:space-y-5 max-w-full mx-auto">
      <div className="flex items-center gap-3 md:gap-4 lg:gap-4 mb-3 md:mb-5 lg:mb-5">
        <Button variant="ghost" size="sm" onClick={handleBackToCashPrize} className="h-10 md:h-12 lg:h-10 text-muted-foreground hover:text-foreground hover:bg-muted/50">
          <ArrowLeft className="h-5 w-5 md:h-6 md:w-6 lg:h-5 lg:w-5" />
        </Button>
        <div className="flex items-center gap-2 md:gap-3 lg:gap-3">
          <CreditCard className="h-6 w-6 md:h-8 md:w-8 lg:h-6 lg:w-6 text-purple-400" />
          <h3 className="font-black text-base md:text-xl lg:text-lg text-foreground">Add Debit Card</h3>
        </div>
      </div>

      <div className="space-y-4 md:space-y-5 lg:space-y-5">
        <div>
          <Label htmlFor="withdrawCardNumber" className="text-sm md:text-base lg:text-base font-black text-foreground uppercase tracking-wide">Card Number</Label>
          <div 
            ref={cardWrapperRef}
            className="coinflow-card-wrapper mt-2" 
            style={{ 
              height: '3rem',
              borderRadius: '0.5rem',
              border: '1px solid hsl(var(--border))',
              backgroundColor: 'hsl(var(--input))',
              transition: 'all 0.2s',
              overflow: 'hidden'
            }}
          >
            <CoinflowCardNumberInput
              merchantId={merchantId}
              env={environment === 'production' ? 'prod' : 'sandbox'}
              ref={coinflowCardNumberRef}
              debug={isStaging}
              css={{
                base: 'height: 3rem; width: 100%; border-radius: 0.5rem; border: none; background-color: transparent; color: hsl(0, 0%, 98%); padding: 0.5rem 0.75rem; font-size: 1rem; line-height: 1.5rem; outline: none; transition: all 0.2s; box-sizing: border-box; caret-color: hsl(0, 0%, 98%);',
                focus: 'outline: none; border: none; box-shadow: none;',
                error: 'border-color: hsl(0, 84%, 60%);',
                cvv: {
                  base: 'height: 3rem; width: 100%; border-radius: 0.5rem; border: none; background-color: transparent; color: hsl(0, 0%, 98%); padding: 0.5rem 0.75rem; font-size: 1rem; line-height: 1.5rem; outline: none; transition: all 0.2s; box-sizing: border-box; caret-color: hsl(0, 0%, 98%);',
                  focus: 'outline: none; border: none; box-shadow: none;',
                  error: 'border-color: hsl(0, 84%, 60%);',
                },
              }}
              origins={[window.location.origin]}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 md:gap-4 lg:gap-4">
          <div>
            <Label htmlFor="withdrawExpMonth" className="text-sm md:text-base lg:text-base font-black text-foreground uppercase tracking-wide">Exp Month</Label>
            <Input
              id="withdrawExpMonth"
              type="text"
              placeholder="MM"
              value={cardForm.expMonth}
              onChange={(e) => setCardForm({ ...cardForm, expMonth: e.target.value.replace(/\D/g, '').slice(0, 2) })}
              maxLength={2}
              disabled={loading}
              required
              className="mt-2 h-12 md:h-14 lg:h-12 text-base md:text-lg lg:text-base"
            />
          </div>
          <div>
            <Label htmlFor="withdrawExpYear" className="text-sm md:text-base lg:text-base font-black text-foreground uppercase tracking-wide">Exp Year</Label>
            <Input
              id="withdrawExpYear"
              type="text"
              placeholder="YY"
              value={cardForm.expYear}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                let year = value.slice(0, 4);
                if (year.length === 4) {
                  year = year.slice(-2); // Take last 2 digits
                } else {
                  year = year.slice(0, 2);
                }
                setCardForm({ ...cardForm, expYear: year });
              }}
              maxLength={4}
              disabled={loading}
              required
              className="mt-2 h-12 md:h-14 lg:h-12 text-base md:text-lg lg:text-base"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="withdrawCvv" className="text-sm md:text-base lg:text-base font-black text-foreground uppercase tracking-wide">CVV</Label>
          <div 
            ref={cvvWrapperRef}
            className="coinflow-cvv-wrapper mt-2"
            style={{
              height: '3rem',
              borderRadius: '0.5rem',
              border: '1px solid hsl(var(--border))',
              backgroundColor: 'hsl(var(--input))',
              transition: 'all 0.2s',
              overflow: 'hidden'
            }}
          >
            <CoinflowCvvInput />
          </div>
        </div>

        <Button 
          className="w-full mt-4 md:mt-6 lg:mt-6 h-12 md:h-16 lg:h-14 bg-gradient-to-r from-green-600 via-emerald-500 to-green-600 hover:from-green-500 hover:via-green-600 hover:to-green-700 text-white font-black rounded-lg md:rounded-xl shadow-xl hover:shadow-2xl border border-green-400/30 hover:border-green-300/50 transition-all duration-200 text-base md:text-lg lg:text-base uppercase tracking-wide" 
          onClick={handleAddDebitCard}
          disabled={!cardForm.expMonth || !cardForm.expYear || loading}
        >
          {loading ? 'Adding...' : 'Add Debit Card'}
        </Button>
      </div>
    </div>
  );

  const [cancellingRequestId, setCancellingRequestId] = useState<string | null>(null);

  const handleCancelRequest = async (requestId: string) => {
    setCancellingRequestId(requestId);
    try {
      const data = await apiClient.put(`/redemption/requests/${requestId}/cancel`);
      if (data?.success) {
        toast({
          title: "Request Cancelled",
          description: data.message || "Your redemption request has been cancelled and SC credited back.",
          variant: "success",
          duration: 4000,
        });
        // Refresh current tab, balance, and limits (SC is credited back on cancel)
        await fetchRequests(requestsTab, 1);
        try {
          const [balanceResponse, limitsData] = await Promise.all([
            apiClient.get('/redemption/balance'),
            apiClient.get('/redemption/limits'),
          ]);
          if (balanceResponse?.balance) {
            setBalanceData({
              sc_bonus_amount: balanceResponse.balance.sc_bonus_amount || 0,
              sc_promotional_amount: balanceResponse.balance.sc_promotional_amount || 0,
              sc_redeemable_amount: balanceResponse.balance.sc_redeemable_amount || 0,
            });
          }
          if (limitsData?.success && limitsData.limits) {
            setWithdrawalLimits(limitsData.limits);
          }
        } catch (e) {
          // Non-critical — will refresh next time modal opens
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel request. Please try again.",
        variant: "destructive",
        duration: 4000,
      });
    } finally {
      setCancellingRequestId(null);
    }
  };

  const renderRequestsView = () => {
    const statusConfig: Record<string, { label: string; color: string }> = {
      pending: { label: 'Pending', color: 'text-yellow-400' },
      processing: { label: 'Processing', color: 'text-blue-400' },
      completed: { label: 'Completed', color: 'text-green-400' },
      failed: { label: 'Failed', color: 'text-red-400' },
      rejected: { label: 'Rejected', color: 'text-red-400' },
      cancelled: { label: 'Cancelled', color: 'text-muted-foreground' },
    };

    const tabs: { key: 'pending' | 'processing' | 'completed' | 'rejected'; label: string; countKeys: string[] }[] = [
      { key: 'pending', label: 'Pending', countKeys: ['pending'] },
      { key: 'processing', label: 'Processing', countKeys: ['processing'] },
      { key: 'completed', label: 'Completed', countKeys: ['completed'] },
      { key: 'rejected', label: 'Rejected', countKeys: ['rejected', 'cancelled', 'failed'] },
    ];

    const formatDate = (dateString: string) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    const formatMethodName = (method: string) => {
      if (method === 'debit_card') return 'Debit Card';
      if (method === 'bank_account') return 'Bank Account';
      return method;
    };

    const formatMethodWithLast4 = (method: string, last4?: string) => {
      const name = formatMethodName(method);
      return last4 ? `${name} ****${last4}` : name;
    };

    const handleTabChange = (tab: 'pending' | 'processing' | 'completed' | 'rejected') => {
      if (tab === requestsTab) return;
      setRequestsTab(tab);
      setExpandedRequestId(null);
      fetchRequests(tab, 1);
    };

    const emptyMessages: Record<string, string> = {
      pending: 'No pending requests',
      processing: 'No requests being processed',
      completed: 'No completed requests yet',
      rejected: 'No rejected or cancelled requests',
    };

    return (
      <div className="space-y-3 md:space-y-4 max-w-full mx-auto">
        <div className="flex items-center gap-2 md:gap-3 mb-1">
          <Button variant="ghost" size="sm" onClick={handleBackToMain} className="h-8 md:h-10 text-muted-foreground hover:text-foreground hover:bg-muted">
            <ArrowLeft className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
          <History className="h-4 w-4 md:h-5 md:w-5 text-emerald-400" />
          <h3 className="font-bold text-sm md:text-lg text-foreground">Requests</h3>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted/20 p-1 rounded-lg md:rounded-xl border border-border/30">
          {tabs.map((tab) => {
            const count = tab.countKeys.reduce((sum, k) => sum + (statusCounts[k] || 0), 0);
            const isActive = requestsTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                className={`flex-1 py-1.5 md:py-2 px-1 rounded-md md:rounded-lg text-[10px] md:text-xs font-semibold transition-all ${
                  isActive
                    ? 'bg-card text-foreground shadow-sm border border-border/50'
                    : 'text-muted-foreground hover:text-foreground/70'
                }`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
                {count > 0 && (
                  <span className={`ml-1 ${isActive ? 'text-muted-foreground' : 'text-muted-foreground/60'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Request list */}
        <div className="space-y-1.5 md:space-y-2.5">
          {loadingRequests ? (
            <div className="space-y-1.5 md:space-y-2.5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="bg-card/50 rounded-lg md:rounded-xl border border-border/40 overflow-hidden animate-pulse">
                  <div className="flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2.5 md:py-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 md:gap-3">
                        <div className="h-3.5 md:h-5 w-20 md:w-28 bg-muted/40 rounded" />
                        <div className="h-3 md:h-4 w-14 md:w-20 bg-muted/30 rounded" />
                      </div>
                      <div className="h-2.5 md:h-4 w-36 md:w-48 bg-muted/25 rounded mt-1.5 md:mt-2" />
                    </div>
                    <div className="h-3.5 w-3.5 md:h-5 md:w-5 bg-muted/30 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 md:py-10 text-muted-foreground">
              <History className="h-8 w-8 md:h-10 md:w-10 mx-auto mb-2 md:mb-3 opacity-30 text-emerald-400/50" />
              <p className="text-xs md:text-sm">{emptyMessages[requestsTab]}</p>
            </div>
          ) : (
            <>
              {requests.map((request) => {
                const isExpanded = expandedRequestId === String(request.id);
                const status = statusConfig[request.status] || { label: request.status, color: 'text-muted-foreground' };

                return (
                  <div key={request.id} className="bg-card/50 rounded-lg md:rounded-xl border border-border/40 overflow-hidden">
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2.5 md:py-4 text-left hover:bg-muted/30 transition-colors"
                      onClick={() => setExpandedRequestId(isExpanded ? null : String(request.id))}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 md:gap-3">
                          <span className="text-xs md:text-base font-semibold text-green-400">SC {formatNumber(request.amount)}</span>
                          {/* Show sub-status label for rejected tab (rejected/cancelled/failed) */}
                          {requestsTab === 'rejected' && (
                            <>
                              <span className="text-[10px] md:text-xs text-muted-foreground">&middot;</span>
                              <span className={`text-[10px] md:text-sm font-semibold ${status.color}`}>{status.label}</span>
                            </>
                          )}
                        </div>
                        <div className="text-[10px] md:text-sm text-muted-foreground mt-0.5 md:mt-1 truncate">
                          {request.withdrawal_method 
                            ? formatMethodWithLast4(request.withdrawal_method, request.withdrawal_method_last4) 
                            : 'Cash'} &middot; {formatDate(request.created_at)}
                        </div>
                      </div>
                      {request.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 md:h-8 md:w-8 p-0 text-muted-foreground hover:text-red-400 hover:bg-red-500/10 rounded-full flex-shrink-0"
                          disabled={cancellingRequestId === String(request.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelRequest(String(request.id));
                          }}
                        >
                          {cancellingRequestId === String(request.id) 
                            ? <div className="h-3 w-3 md:h-3.5 md:w-3.5 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                            : <XCircle className="h-3.5 w-3.5 md:h-4 md:w-4" />}
                        </Button>
                      )}
                      <ChevronDown className={`h-3.5 w-3.5 md:h-5 md:w-5 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {isExpanded && (
                      <div className="px-3 md:px-5 pb-2.5 md:pb-4 pt-0 border-t border-border/30">
                        <div className="space-y-1 md:space-y-2 text-[11px] md:text-sm pt-2 md:pt-3">
                          {request.fee_amount != null && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Fee:</span>
                              <span className="text-foreground">SC {formatNumber(request.fee_amount)}{request.final_amount != null && <> &middot; Net: SC {formatNumber(request.final_amount)}</>}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Requested:</span>
                            <span className="text-foreground">{formatDate(request.created_at)}</span>
                          </div>
                          {request.withdrawal_method && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Method:</span>
                              <span className="text-foreground">{formatMethodWithLast4(request.withdrawal_method, request.withdrawal_method_last4)}</span>
                            </div>
                          )}
                          {request.processed_at && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Processed:</span>
                              <span className="text-foreground">{formatDate(request.processed_at)}</span>
                            </div>
                          )}
                          {request.external_withdrawal_id && (
                            <div>
                              <span className="text-muted-foreground">Txn ID:</span>
                              <div className="font-mono text-[10px] md:text-xs text-muted-foreground/70 mt-0.5 bg-muted/20 px-2 py-1 rounded break-all leading-relaxed select-all">{request.external_withdrawal_id}</div>
                            </div>
                          )}
                        </div>

                        {request.status === 'pending' && (
                          <div className="mt-2 md:mt-3">
                            <div className="px-2 md:px-3 py-1.5 md:py-2.5 bg-yellow-500/10 border border-yellow-500/20 rounded md:rounded-lg text-[11px] md:text-sm text-yellow-300/80">
                              Awaiting admin approval.
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="mt-2 md:mt-2.5 w-full h-7 md:h-9 text-[11px] md:text-sm text-muted-foreground hover:text-red-300 hover:bg-red-500/10 rounded md:rounded-lg font-medium"
                              disabled={cancellingRequestId === String(request.id)}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelRequest(String(request.id));
                              }}
                            >
                              {cancellingRequestId === String(request.id) ? 'Cancelling...' : 'Cancel Request'}
                            </Button>
                          </div>
                        )}
                        {request.status === 'cancelled' && (
                          <div className="mt-2 md:mt-3 px-2 md:px-3 py-1.5 md:py-2.5 bg-muted/30 border border-border rounded md:rounded-lg text-[11px] md:text-sm text-foreground/80">
                            Cancelled. SC credited back to your balance.
                          </div>
                        )}
                        {request.status === 'rejected' && request.rejection_reason && (
                          <div className="mt-2 md:mt-3 px-2 md:px-3 py-1.5 md:py-2.5 bg-red-500/10 border border-red-500/20 rounded md:rounded-lg text-[11px] md:text-sm">
                            <span className="font-semibold text-red-300">Reason:</span>{' '}
                            <span className="text-red-300/70">{request.rejection_reason}</span>
                          </div>
                        )}
                        {request.status === 'processing' && (
                          <div className="mt-2 md:mt-3 px-2 md:px-3 py-1.5 md:py-2.5 bg-blue-500/10 border border-blue-500/20 rounded md:rounded-lg text-[11px] md:text-sm text-blue-300/80">
                            {request.withdrawal_method === 'debit_card'
                              ? 'Being processed. Funds will arrive within ~30 minutes.'
                              : 'Being processed. Funds will arrive within 3-10 business days.'}
                          </div>
                        )}
                        {request.status === 'completed' && (
                          <div className="mt-2 md:mt-3 px-2 md:px-3 py-1.5 md:py-2.5 bg-green-500/10 border border-green-500/20 rounded md:rounded-lg text-[11px] md:text-sm text-green-300/80">
                            Complete. Funds should be in your account.
                          </div>
                        )}
                        {request.status === 'failed' && request.failure_reason && (
                          <div className="mt-2 md:mt-3 px-2 md:px-3 py-1.5 md:py-2.5 bg-red-500/10 border border-red-500/20 rounded md:rounded-lg text-[11px] md:text-sm">
                            <span className="font-semibold text-red-300">Failed:</span>{' '}
                            <span className="text-red-300/70">{request.failure_reason}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Load More */}
              {requestsHasMore && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full h-8 md:h-10 text-xs md:text-sm text-muted-foreground hover:text-foreground rounded-lg font-medium"
                  disabled={loadingMoreRequests}
                  onClick={() => fetchRequests(requestsTab, requestsPage + 1, true)}
                >
                  {loadingMoreRequests ? 'Loading...' : 'Load More'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const handleKYCSuccess = async () => {
    // Refresh withdrawal methods after successful KYC registration
    setIsUserRegistered(true);
    try {
      const withdrawerData = await apiClient.get('/customer/withdrawal-methods');
      const accounts: any[] = [];
      
      if (withdrawerData?.bankAccounts) {
        withdrawerData.bankAccounts.forEach((acc: any) => {
          if (!acc.token) {
            return; // Skip accounts without tokens
          }
          accounts.push({
            id: acc.id || acc._id || `bank_${acc.token}`,
            type: 'bank_account',
            name: acc.bankName || 'Bank Account',
            details: `****${acc.last4 || '****'}`,
            accountId: acc.token, // Token is required for withdrawals
            token: acc.token, // Token is required for deletion
            isPrimary: accounts.length === 0,
          });
        });
      }
      
      if (withdrawerData?.cards) {
        withdrawerData.cards.forEach((card: any) => {
          if (!card.token) {
            return; // Skip cards without tokens
          }
          accounts.push({
            id: card.id || card._id || `card_${card.token}`,
            type: 'debit_card',
            name: `${card.brand || 'Card'} Debit`,
            details: `****${card.last4 || '****'}`,
            accountId: card.token, // Token is required for withdrawals
            token: card.token, // Token is required for deletion
            isPrimary: accounts.length === 0,
          });
        });
      }
      
      setWithdrawalMethods(accounts);
      
      // After KYC success, auto-select the first method matching the stored type and navigate
      if (selectedMethodType) {
        const firstOfType = accounts.find(a => a.type === selectedMethodType);
        if (firstOfType) {
          setSelectedPaymentMethod(String(firstOfType.id));
        }
      }
      
      // Navigate to the cashPrize form so the user continues seamlessly
      setCurrentView('cashPrize');
    } catch {
      // Still navigate — user is registered, they can add methods from the form
      setCurrentView('cashPrize');
    }
  };


  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => {
        // Only allow closing, not opening via Dialog's onOpenChange
        if (!open) {
          onClose();
        }
      }}>
        <DialogContent 
          ref={modalContentRef}
          className="!fixed !left-0 !top-0 !right-0 !bottom-0 !w-[100vw] !h-[100vh] !max-w-none !max-h-none lg:!left-[50%] lg:!top-[50%] lg:!right-auto lg:!bottom-auto lg:!w-[95vw] lg:!max-w-xl lg:!h-[85vh] lg:!max-h-[750px] m-0 p-0 rounded-none lg:rounded-2xl border-0 lg:border lg:border-border bg-background overflow-hidden !translate-x-0 translate-y-0 lg:!translate-x-[-50%] lg:!translate-y-[-50%] flex flex-col [&>button]:hidden pt-[env(safe-area-inset-top)] lg:pt-0 pb-[env(safe-area-inset-bottom)] lg:pb-0 lg:shadow-2xl lg:shadow-black/50"
          style={{
            '--tw-translate-y': dragY > 0 ? `${dragY}px` : '0px',
            opacity: dragY > 0 ? Math.max(0.6, 1 - dragY / 400) : undefined,
            transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            overscrollBehaviorY: 'none',
          } as React.CSSProperties}
          aria-describedby={undefined}
        >
          {/* Drag Handle Indicator - Mobile & Tablet Only */}
          <div 
            className="lg:hidden pt-3 md:pt-4 pb-2 md:pb-3 flex justify-center touch-none cursor-grab active:cursor-grabbing select-none"
            onTouchStart={handleDragHandleTouchStart}
            onTouchMove={handleDragHandleTouchMove}
            onTouchEnd={handleDragHandleTouchEnd}
          >
            <div 
              className="w-10 md:w-14 h-1.5 bg-muted-foreground/50 rounded-full transition-all duration-200"
              style={{
                opacity: isDragging ? 0.6 : 1,
                transform: isDragging ? 'scaleX(0.6)' : 'scaleX(1)',
                backgroundColor: isDragging ? 'rgba(128, 128, 128, 0.7)' : undefined,
                transition: isDragging ? 'none' : 'all 0.2s ease',
              }}
            />
          </div>
          
          {/* Close button - wrapped in div to avoid [&>button]:hidden selector */}
          <div className="absolute right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] md:right-5 md:top-[calc(env(safe-area-inset-top)+1.25rem)] lg:right-4 lg:top-4 z-20">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="flex h-9 w-9 md:h-11 md:w-11 lg:h-10 lg:w-10 rounded-full bg-muted hover:bg-muted/80 border border-border"
              aria-label="Close"
            >
              <X className="h-4 w-4 md:h-5 md:w-5 lg:h-5 lg:w-5 text-foreground/80" />
            </Button>
          </div>

          <div className="px-5 md:px-7 lg:px-6 pb-20 md:pb-24 lg:pb-6 pt-4 md:pt-6 lg:pt-6 flex-1 overflow-y-auto min-h-0">
            {currentView === 'main' && renderMainView()}
            {currentView === 'cashPrize' && renderCashPrizeView()}
            {currentView === 'addBank' && renderAddBankView()}
            {currentView === 'addCard' && renderAddCardView()}
            {currentView === 'requests' && renderRequestsView()}
          </div>
        </DialogContent>
      </Dialog>

      <KYCSSNModal
        isOpen={isKYCModalOpen}
        onClose={() => setIsKYCModalOpen(false)}
        onSuccess={handleKYCSuccess}
      />

      {/* Bank Account Linking — opens like Coinflow hosted checkout */}
      <Dialog open={bankAuthDialogOpen} onOpenChange={(open) => {
        if (!open) closeBankAuthDialog();
      }}>
        <DialogContent
          className="max-w-lg w-full p-0 gap-0 border-0 bg-transparent overflow-hidden [&>button]:hidden"
          aria-describedby={undefined}
        >
          <div className="relative w-full min-h-[500px]">
            {/* Iframe — sizes itself like Coinflow checkout */}
            {bankAuthIframeUrl && (
              <iframe
                src={bankAuthIframeUrl}
                title="Link Bank Account"
                className="w-full border-none"
                style={{
                  width: '100%',
                  height: '100vh',
                  maxHeight: '800px',
                  minHeight: '500px',
                  opacity: bankAuthIframeLoaded ? 1 : 0,
                  transition: 'opacity 300ms linear',
                }}
                allow="payment;camera;clipboard-write"
                onLoad={() => setBankAuthIframeLoaded(true)}
              />
            )}

            {/* Loading overlay — matches Coinflow hosted checkout branding */}
            {!bankAuthIframeLoaded && (
              <div
                className={`absolute top-0 left-0 w-full h-full bg-slate-950 flex flex-col items-center justify-center z-10 transition-opacity duration-500 ease-out ${
                  bankAuthIframeLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'
                }`}
              >
                <div className="relative mb-4">
                  <div 
                    className="absolute inset-0 blur-2xl opacity-30"
                    style={{
                      background: 'radial-gradient(circle, rgba(251,191,36,0.4) 0%, transparent 70%)',
                      width: 120,
                      height: 120,
                      left: -30,
                      top: -30,
                    }}
                  />
                  <img
                    src={logoSrc}
                    alt="Gold Machine"
                    width={60}
                    height={60}
                    className="relative z-10 animate-pulse object-contain"
                    style={{ animationDuration: '2s' }}
                  />
                </div>
                <p className="text-sm text-amber-400/80 font-medium mb-3">Loading secure bank linking...</p>
                <div className="flex items-center gap-1">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-amber-500/60"
                      style={{
                        animation: 'bankauth-dot-pulse 1.2s infinite ease-in-out',
                        animationDelay: `${i * 0.15}s`,
                      }}
                    />
                  ))}
                </div>
                <style>{`
                  @keyframes bankauth-dot-pulse {
                    0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
                    40% { opacity: 1; transform: scale(1); }
                  }
                `}</style>
              </div>
            )}

            {/* Error state */}
            {!bankAuthIframeUrl && !bankAuthLoading && bankAuthDialogOpen && (
              <div className="absolute inset-0 bg-black/90 rounded-lg flex flex-col items-center justify-center min-h-[300px]">
                <p className="text-sm text-muted-foreground mb-3">Failed to load bank linking.</p>
                <Button variant="outline" size="sm" onClick={closeBankAuthDialog}>
                  Close
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};